import { Injectable } from '@angular/core';
import { Observable, take, withLatestFrom, mergeMap, of } from 'rxjs';

import {
  DprActivityGridData,
  DprForecastActivityGridData,
} from '@dpr-details/models/grid';
import {
  DprActivitiesGridSync,
  DprHseGridSync,
  DprPersonnelGridSync,
  DprWeatherGridSync,
  DprConsumableGridSync,
  DprSummaryGridSync,
  DprProgressGridSync,
  DprAppendixGridSync,
  DprHiddenGridSync,
  DprStatusTrackerGridSync,
  DprProgressWithoutTargetGridSync,
  DprForecastActivitiesGridSync,
  DprFreeTextFieldSync,
  DprTableOfColumnGridSync,
} from '@dpr-details/models/sync';
import {
  DprDetailsSync,
  DprDetails,
  DprWeatherDetail,
  DprConsumableDetail,
  DprPersonnelDetail,
  DprSummaryAttachment,
  DprHidden,
  DprProgressDetail,
  DprHseDetail,
  DprStatusTrackerDetails,
  DprTableOfColumnDetail,
  DprProgressWithoutTargetDetail,
  DprFreeTextFieldDetail,
} from '@shared/models/dpr';
import { DprAttachmentsGridData, Sync } from '@shared/models/grid';
import { ActivityStream } from '@shared/models/project-setup';
import { SyncService } from '@shared/services/grid';
import { OfflineFacade, UserDetailsFacade } from '@shared/state/facades';
import { DprDetailsSyncSanitizeService } from '.';

@Injectable()
export class DprDetailsSyncModelBuilderService {
  constructor(
    private syncService: SyncService,
    private dprDetailsSyncSanitizeService: DprDetailsSyncSanitizeService,
    private offlineFacade: OfflineFacade,
    private userDetailsFacade: UserDetailsFacade
  ) {}

  forDprDetailId(dprDetailId: number | string): Observable<DprDetailsSync> {
    return this.offlineFacade.getDprDetails(dprDetailId).pipe(
      take(1),
      withLatestFrom(this.userDetailsFacade.query.accountId$),
      mergeMap(([dprDetails, accountId]) => {
        return of(this.forDprDetails(dprDetails, accountId));
      })
    );
  }

  forDprDetails(dprDetails: DprDetails, accountId: string): DprDetailsSync {
    return {
      id: dprDetails.id,
      dprId: dprDetails.dprId,
      dprDisplayId: dprDetails.dprDisplayId,
      dprSubprojectId: dprDetails.dprSubprojectId,
      dprDateTime: dprDetails.dprDateTime,
      revision: dprDetails.revision,
      isSignDprDetailsUploaded: dprDetails.isSignDprDetailsUploaded,
      accountId,
      lockedDate: dprDetails.lockedDate,
    };
  }

  forDprActivities(
    subprojectId: number,
    dprDetailsId: number | string,
    activityStreams: ActivityStream[],
    changesMap: Map<number, Sync<DprActivityGridData>>
  ): DprActivitiesGridSync {
    let dprActivitiesChanges: Sync<DprActivityGridData> =
      this.syncService.getDefaultSyncModel();

    let dprParallelActivitiesChanges: Sync<DprActivityGridData> =
      this.syncService.getDefaultSyncModel();

    for (const key of changesMap.keys()) {
      const activityStream = activityStreams.find((x) => x.id === key);
      const changes = changesMap.get(key);

      if (activityStream.parallelActivityType === 0) {
        dprActivitiesChanges = changes;
      } else {
        dprParallelActivitiesChanges = this.syncService.mergeModels([
          dprParallelActivitiesChanges,
          changes,
        ]);
      }
    }

    return {
      subprojectId,
      dprDetailsId,
      syncDPRActivities: this.syncService.sanitizeModel(
        dprActivitiesChanges,
        this.dprDetailsSyncSanitizeService.sanitizeActivity.bind(
          this.dprDetailsSyncSanitizeService
        )
      ),
      syncDPRParallelActivities: this.syncService.sanitizeModel(
        dprParallelActivitiesChanges,
        this.dprDetailsSyncSanitizeService.sanitizeParallelActivity.bind(
          this.dprDetailsSyncSanitizeService
        )
      ),
    };
  }

  forDprStatusTracker(
    subprojectId: number,
    dprStatusTrackerId: number | string,
    dprDetailId: number | string,
    dprStatusTrackerDetailsChanges: Sync<DprStatusTrackerDetails>
  ): DprStatusTrackerGridSync {
    return {
      subprojectId,
      dprStatusTracker: {
        id: dprStatusTrackerId,
        dprDetailId,
        syncDprStatusTrackerDetails: this.syncService.sanitizeModel(
          dprStatusTrackerDetailsChanges,
          this.dprDetailsSyncSanitizeService.sanitizeStatusTrackerDetailItem
        ),
      },
    };
  }

  forDprWeather(
    subprojectId: number,
    dprDetailId: number | string,
    dprWeatherId: number | string,
    dprWeatherDetailsChanges: Sync<DprWeatherDetail>,
    dprWeatherAttachmentsChanges: Sync<DprAttachmentsGridData>,
    comment: string
  ): DprWeatherGridSync {
    return {
      subprojectId,
      dprWeather: {
        id: dprWeatherId,
        comment,
        dprDetailId,
        syncDprWeatherDetails: this.syncService.sanitizeModel(
          dprWeatherDetailsChanges,
          this.dprDetailsSyncSanitizeService.sanitizeWeatherDetailItem
        ),
        syncDprWeatherAttachments: this.syncService.sanitizeModel(
          dprWeatherAttachmentsChanges,
          this.dprDetailsSyncSanitizeService.sanitizeAttachment.bind(
            this.dprDetailsSyncSanitizeService
          )
        ),
      },
    };
  }

  forDprHse(
    subprojectId: number,
    dprDetailId: number | string,
    dprHseId: number | string,
    dprHseDetailsChanges: Sync<DprHseDetail>,
    dprHseAttachmentsChanges: Sync<DprAttachmentsGridData>,
    comment: string
  ): DprHseGridSync {
    return {
      subprojectId,
      dprHse: {
        id: dprHseId,
        comment,
        dprDetailId,
        syncDprHseDetails: this.syncService.sanitizeModel(
          dprHseDetailsChanges,
          this.dprDetailsSyncSanitizeService.sanitizeHseDetailItem
        ),
        syncDprHseAttachments: this.syncService.sanitizeModel(
          dprHseAttachmentsChanges,
          this.dprDetailsSyncSanitizeService.sanitizeAttachment.bind(
            this.dprDetailsSyncSanitizeService
          )
        ),
      },
    };
  }

  forDprPersonnel(
    subprojectId: number,
    dprDetailId: number | string,
    dprPersonnelId: number | string,
    dprPersonnelDetailsChanges: Sync<DprPersonnelDetail>,
    dprPersonnelAttachmentsChanges: Sync<DprAttachmentsGridData>,
    comment: string
  ): DprPersonnelGridSync {
    return {
      subprojectId,
      dprPersonnel: {
        id: dprPersonnelId,
        comment,
        dprDetailId,
        syncDprPersonnelDetails: this.syncService.sanitizeModel(
          dprPersonnelDetailsChanges,
          this.dprDetailsSyncSanitizeService.sanitizePersonnelDetailItem
        ),
        syncDprPersonnelAttachments: this.syncService.sanitizeModel(
          dprPersonnelAttachmentsChanges,
          this.dprDetailsSyncSanitizeService.sanitizeAttachment.bind(
            this.dprDetailsSyncSanitizeService
          )
        ),
      },
    };
  }

  forDprConsumable(
    subprojectId: number,
    dprDetailId: number | string,
    dprConsumableId: number | string,
    syncDprConsumableDetails: Sync<DprConsumableDetail>,
    syncDprConsumableAttachments: Sync<DprAttachmentsGridData>,
    comment: string
  ): DprConsumableGridSync {
    return {
      subprojectId,
      dprConsumable: {
        id: dprConsumableId,
        comment,
        dprDetailId,
        syncDprConsumableDetails: this.syncService.sanitizeModel(
          syncDprConsumableDetails,
          this.dprDetailsSyncSanitizeService.sanitizeConsumableDetailItem
        ),
        syncDprConsumableAttachments: this.syncService.sanitizeModel(
          syncDprConsumableAttachments,
          this.dprDetailsSyncSanitizeService.sanitizeAttachment.bind(
            this.dprDetailsSyncSanitizeService
          )
        ),
      },
    };
  }

  forDprSummary(
    subprojectId: number,
    dprDetailId: number | string,
    dprSummaryId: number | string,
    syncDprSummaryAttachments: Sync<DprSummaryAttachment>,
    currentComment: string,
    nextComment: string,
    externalComment: string
  ): DprSummaryGridSync {
    return {
      subprojectId,
      dprSummary: {
        id: dprSummaryId,
        dprDetailId,
        currentComment,
        nextComment,
        externalComment,
        syncDprSummaryAttachments,
      },
    };
  }

  forDprForecastActivities(
    subprojectId: number,
    dprDetailsId: number | string,
    forecastActivityChanges: Sync<DprForecastActivityGridData>
  ): DprForecastActivitiesGridSync {
    return {
      subprojectId,
      dprDetailsId,
      syncDprForecastActivities: this.syncService.sanitizeModel(
        forecastActivityChanges,
        this.dprDetailsSyncSanitizeService.sanitizeForecastActivity.bind(
          this.dprDetailsSyncSanitizeService
        )
      ),
    };
  }

  forDprAppendix(
    subprojectId: number,
    dprDetailId: number | string,
    syncDprAppendixAttachments: Sync<DprAttachmentsGridData>
  ): DprAppendixGridSync {
    return {
      dprDetailId,
      subprojectId,
      syncDprAppendixAttachments: this.syncService.sanitizeModel(
        syncDprAppendixAttachments,
        this.dprDetailsSyncSanitizeService.sanitizeAttachment.bind(
          this.dprDetailsSyncSanitizeService
        )
      ),
    };
  }

  forDprFreeTextFields(
    subprojectId: number,
    dprDetailId: number | string,
    dprFreeTextFieldDetails: Sync<DprFreeTextFieldDetail>,
    freeTextFieldId: number,
    dprFreeTextFieldId: number | string
  ): DprFreeTextFieldSync {
    return {
      subprojectId,
      dprFreeTextField: {
        id: dprFreeTextFieldId,
        dprDetailId,
        freeTextFieldId,
        syncDprFreeTextFieldDetails: this.syncService.sanitizeModel(
          dprFreeTextFieldDetails,
          this.dprDetailsSyncSanitizeService.sanitizeFreeTextField.bind(
            this.dprDetailsSyncSanitizeService
          )
        ),
      },
    };
  }

  forDprProgress(
    subprojectId: number,
    dprDetailId: number | string,
    dprProgressId: number | string,
    dprProgressDetails: Sync<DprProgressDetail>,
    dprProgressAttachments: Sync<DprAttachmentsGridData>,
    comment: string
  ): DprProgressGridSync {
    return {
      subprojectId,
      dprprogress: {
        id: dprProgressId,
        comment,
        dprDetailId,
        syncDprProgressDetails: this.syncService.sanitizeModel(
          dprProgressDetails,
          this.dprDetailsSyncSanitizeService.sanitizeProgressDetails
        ),
        syncDprProgressAttachments: this.syncService.sanitizeModel(
          dprProgressAttachments,
          this.dprDetailsSyncSanitizeService.sanitizeAttachment.bind(
            this.dprDetailsSyncSanitizeService
          )
        ),
      },
    };
  }

  forDprProgressWithoutTarget(
    subprojectId: number,
    dprDetailId: number | string,
    dprProgressWithoutTargetId: number | string,
    dprProgressWithoutTargetDetails: Sync<DprProgressWithoutTargetDetail>,
    dprProgressWithoutTargetAttachments: Sync<DprAttachmentsGridData>,
    comment: string
  ): DprProgressWithoutTargetGridSync {
    return {
      subprojectId,
      dprProgressWithoutTarget: {
        id: dprProgressWithoutTargetId,
        comment,
        dprDetailId,
        syncDprProgressWithoutTargetDetails: this.syncService.sanitizeModel(
          dprProgressWithoutTargetDetails,
          this.dprDetailsSyncSanitizeService
            .sanitizeProgressWithoutTargetDetails
        ),
        syncDprProgressWithoutTargetAttachments: this.syncService.sanitizeModel(
          dprProgressWithoutTargetAttachments,
          this.dprDetailsSyncSanitizeService.sanitizeAttachment.bind(
            this.dprDetailsSyncSanitizeService
          )
        ),
      },
    };
  }

  forDprTableOfColumns(
    subprojectId: number,
    dprDetailId: number | string,
    dprTableOfColumnId: number | string,
    tableOfColumnId: number,
    dprTableOfColumnDetails: Sync<DprTableOfColumnDetail>,
    dprTableOfColumnAttachments: Sync<DprAttachmentsGridData>,
    comment: string
  ): DprTableOfColumnGridSync {
    return {
      subprojectId,
      dprTableOfColumn: {
        id: dprTableOfColumnId,
        comment,
        dprDetailId,
        tableOfColumnId,
        syncDprTableOfColumnDetails: this.syncService.sanitizeModel(
          dprTableOfColumnDetails,
          this.dprDetailsSyncSanitizeService.sanitizeTableOfColumnDetailItem.bind(
            this.dprDetailsSyncSanitizeService
          )
        ),
        syncDprTableOfColumnAttachments: this.syncService.sanitizeModel(
          dprTableOfColumnAttachments,
          this.dprDetailsSyncSanitizeService.sanitizeAttachment.bind(
            this.dprDetailsSyncSanitizeService
          )
        ),
      },
    };
  }

  forDprHidden(
    subprojectId: number,
    dprDetailId: number | string,
    dprHiddenChanges: Sync<DprHidden>
  ): DprHiddenGridSync {
    return {
      subprojectId,
      dprDetailId,
      syncDprHidden: this.syncService.sanitizeModel(
        dprHiddenChanges,
        this.dprDetailsSyncSanitizeService.sanitizeHidden.bind(
          this.dprDetailsSyncSanitizeService
        )
      ),
    };
  }
}
